// Przekrój sprzedaży (owner preview 2026-10-26, periods and filters
// 2026-10-27): the orders of „Zamówienia w wyniku” grouped by account
// manager, customer or product, one or two levels, with the orders as leaves,
// in one total column or per month / quarter / year. Pure folding: money comes
// from the order rows per month (invoiced = the actual part, waiting = the
// forecast part, order-margin basis); a product gets its share of each order
// by sale value — the same rule as Analiza (`buildProductAnalysisOrders`).
import type { OrderInResultRow } from './ordersInResult';
import {
  addLedgerMonth, addPlanPending, emptyPendingScope, mergePendingScope, pendingOrderIds,
  type PendingDeltas, type PendingScope,
} from '../pendingScope';

export type PivotDimension = 'manager' | 'customer' | 'product';
export type PivotColumns = 'total' | 'month' | 'quarter' | 'year';
export type PivotScope = 'all' | 'actual' | 'forecast';
export type PivotMeasure = 'revenue' | 'cost' | 'margin' | 'marginPercent' | 'orders';

export interface PivotOrderInfo {
  accountManagerName: string | null;
  products: { key: string; label: string; value: number }[];
}

export interface PivotFilters {
  manager: string[];
  customer: string[];
  product: string[];
  /** Order statuses (`CONFIRMED`, …). */
  status: string[];
  /** Leave out orders without a cost or with an unknown part of it (flags NO_COST / COST_UNKNOWN). */
  hideNoCost: boolean;
}

export const NO_PIVOT_FILTERS: PivotFilters = { manager: [], customer: [], product: [], status: [], hideNoCost: false };

export interface PivotMoney {
  orderIds: Set<string>;
  invoiced: number;
  waiting: number;
  /** Sum of the margins of the known costs (an incomplete cost counts its known part). */
  margin: number;
  /** Cost of the same known part (revenue − margin where the margin is known); partial with `marginIncomplete`. */
  cost: number;
  /** The settled part of `cost`. */
  costActual: number;
  /** The settled part of `margin` (the chart keeps settled and forecast apart). */
  marginActual: number;
  /** Some order (or its share) has an unknown or only partly known cost: the margin is partial. */
  marginIncomplete: boolean;
  /** Orders (or shares) with an unknown or only partly known cost in the cell, after its months are summed. */
  pendingOrderIds: Set<string>;
  /** What decides the two above (one „*” rule for every range, `pendingScope.ts`). */
  pending: PendingScope;
}

export interface PivotCells {
  total: PivotMoney;
  /** Keyed by bucket (`2026-03`, `2026-Q1`, `2026`); empty in the total view. */
  byBucket: Map<string, PivotMoney>;
}

export interface PivotOrderLeaf extends PivotCells {
  row: OrderInResultRow;
  share: number;
}

export interface PivotNode extends PivotMoney, PivotCells {
  key: string;
  label: string;
  children: PivotNode[];
  /** Orders on the deepest level. */
  orders: PivotOrderLeaf[];
}

export interface PivotOptions {
  first: PivotDimension;
  second: PivotDimension | null;
  columns?: PivotColumns;
  scope?: PivotScope;
  filters?: PivotFilters;
  unknownProduct?: string;
}

export interface PivotResult extends PivotCells {
  nodes: PivotNode[];
  /** Continuous, ascending; empty in the total view. */
  buckets: string[];
}

export interface PivotFilterOption {
  key: string;
  label: string;
}

interface Identity {
  key: string;
  label: string;
}

/** One order (or its product share) in one month, actual or forecast. */
interface PivotFact {
  row: OrderInResultRow;
  share: number;
  month: string;
  kind: 'actual' | 'forecast';
  revenue: number;
  margin: number | null;
  /** Only the known part of the cost is in `margin` (this month alone). */
  costPending: boolean;
  /** Actual: the month's change of pending invoice lines. */
  pendingDeltas?: PendingDeltas;
  keys: Record<PivotDimension, Identity>;
}

const NO_MANAGER: Identity = { key: 'none', label: 'Bez opiekuna' };

function emptyMoney(): PivotMoney {
  return {
    orderIds: new Set<string>(), invoiced: 0, waiting: 0, margin: 0, cost: 0, costActual: 0, marginActual: 0,
    marginIncomplete: false, pendingOrderIds: new Set<string>(), pending: emptyPendingScope(),
  };
}

function emptyCells(): PivotCells {
  return { total: emptyMoney(), byBucket: new Map() };
}

export function bucketOf(month: string, columns: PivotColumns): string {
  if (columns === 'year') return month.slice(0, 4);
  if (columns === 'quarter') return `${month.slice(0, 4)}-Q${Math.floor((Number(month.slice(5, 7)) - 1) / 3) + 1}`;
  if (columns === 'month') return month.slice(0, 7);
  return 'total';
}

function nextBucket(bucket: string, columns: PivotColumns): string {
  const year = Number(bucket.slice(0, 4));
  if (columns === 'year') return String(year + 1);
  if (columns === 'quarter') {
    const quarter = Number(bucket.slice(6));
    return quarter === 4 ? `${year + 1}-Q1` : `${year}-Q${quarter + 1}`;
  }
  const month = Number(bucket.slice(5, 7));
  return month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`;
}

function bucketRange(first: string, last: string, columns: PivotColumns): string[] {
  const list = [first];
  while (list[list.length - 1] < last && list.length < 240) list.push(nextBucket(list[list.length - 1], columns));
  return list;
}

function orderProducts(order: PivotOrderInfo | undefined, unknownProduct: string): { identity: Identity; share: number }[] {
  const products = order?.products.filter((product) => product.value > 0) || [];
  const total = products.reduce((sum, product) => sum + product.value, 0);
  if (!(total > 0)) return [{ identity: { key: 'unknown', label: unknownProduct }, share: 1 }];
  // Same product twice on one order (two lines) adds up to one share.
  const byKey = new Map<string, { label: string; value: number }>();
  products.forEach((product) => {
    const current = byKey.get(product.key);
    byKey.set(product.key, { label: product.label, value: (current?.value || 0) + product.value });
  });
  return Array.from(byKey.entries()).map(([key, product]) => ({ identity: { key, label: product.label }, share: product.value / total }));
}

function facts(rows: OrderInResultRow[], info: Map<string, PivotOrderInfo>, withProducts: boolean, unknownProduct: string, scope: PivotScope): PivotFact[] {
  return rows.filter((row) => row.place !== 'OUTSIDE').flatMap((row) => {
    const order = info.get(row.id);
    const manager = order?.accountManagerName ? { key: order.accountManagerName, label: order.accountManagerName } : NO_MANAGER;
    const customer = { key: row.customerName, label: row.customerName };
    const products = withProducts ? orderProducts(order, unknownProduct) : [{ identity: { key: '', label: '' }, share: 1 }];
    const parts: { kind: PivotFact['kind']; part: OrderInResultRow['actual'] }[] = [];
    if (scope !== 'forecast') parts.push({ kind: 'actual', part: row.actual });
    if (scope !== 'actual') parts.push({ kind: 'forecast', part: row.forecast });
    return parts.flatMap(({ kind, part }) => {
      if (!part) return [];
      // A part without months (older rows) sits in its first month or nowhere.
      const months = part.byMonth?.length
        ? part.byMonth
        : [{ month: part.months[0] || '', revenue: part.revenue, margin: part.margin, costPending: part.costPending }];
      return months.flatMap((slot) => products.map(({ identity, share }) => ({
        row, share, month: slot.month, kind, revenue: slot.revenue * share,
        margin: slot.margin == null ? null : slot.margin * share,
        costPending: Boolean(slot.costPending),
        ...('pendingDeltas' in slot && slot.pendingDeltas ? { pendingDeltas: slot.pendingDeltas } : {}),
        keys: { manager, customer, product: identity },
      })));
    });
  });
}

// Rule (decision 2026-10-29 pt 8): in every cell cost = revenue − margin and
// margin % = margin / revenue of the same amounts. An incomplete cost counts
// its known part (an unknown one: 0) and marks the cell (the percent is
// overstated).
function addFact(money: PivotMoney, fact: PivotFact): void {
  money.orderIds.add(fact.row.id);
  if (fact.kind === 'actual') money.invoiced += fact.revenue;
  else money.waiting += fact.revenue;
  const pending = fact.margin == null || fact.costPending;
  // Unknown cost: its known part is 0, so the margin equals the revenue.
  const margin = fact.margin ?? fact.revenue;
  // „*” after the cell's months are summed: an invoice completed by a later
  // WZ in the same cell no longer marks it.
  if (fact.kind === 'actual') addLedgerMonth(money.pending, fact.row.id, fact.pendingDeltas, pending);
  else if (pending) addPlanPending(money.pending, fact.row.id);
  money.pendingOrderIds = pendingOrderIds(money.pending);
  money.marginIncomplete = money.pendingOrderIds.size > 0;
  money.margin += margin;
  money.cost += fact.revenue - margin;
  if (fact.kind === 'actual') {
    money.marginActual += margin;
    money.costActual += fact.revenue - margin;
  }
}



function addToCells(cells: PivotCells, fact: PivotFact, columns: PivotColumns): void {
  addFact(cells.total, fact);
  if (columns === 'total') return;
  const bucket = bucketOf(fact.month, columns);
  const money = cells.byBucket.get(bucket) || emptyMoney();
  addFact(money, fact);
  cells.byBucket.set(bucket, money);
}

function group(list: PivotFact[], dimension: PivotDimension, next: PivotDimension | null, columns: PivotColumns): PivotNode[] {
  const nodes = new Map<string, { identity: Identity; cells: PivotCells; facts: PivotFact[] }>();
  for (const fact of list) {
    const identity = fact.keys[dimension];
    const node = nodes.get(identity.key) || { identity, cells: emptyCells(), facts: [] };
    addToCells(node.cells, fact, columns);
    node.facts.push(fact);
    nodes.set(identity.key, node);
  }
  return Array.from(nodes.values())
    .map(({ identity, cells, facts: own }) => ({
      key: identity.key,
      label: identity.label,
      ...cells.total,
      total: cells.total,
      byBucket: cells.byBucket,
      children: next ? group(own, next, null, columns) : [],
      orders: next ? [] : leaves(own, columns),
    }))
    .sort((a, b) => (b.invoiced + b.waiting) - (a.invoiced + a.waiting));
}

function leaves(list: PivotFact[], columns: PivotColumns): PivotOrderLeaf[] {
  const byOrder = new Map<string, PivotOrderLeaf>();
  for (const fact of list) {
    // Every fact of one order under one product carries the same share of it.
    const leaf = byOrder.get(fact.row.id) || { row: fact.row, share: fact.share, ...emptyCells() };
    addToCells(leaf, fact, columns);
    byOrder.set(fact.row.id, leaf);
  }
  return Array.from(byOrder.values())
    .sort((a, b) => (b.total.invoiced + b.total.waiting) - (a.total.invoiced + a.total.waiting));
}

function matchesFilters(fact: PivotFact, filters: PivotFilters): boolean {
  return (!filters.status.length || filters.status.includes(fact.row.status))
    && (!filters.hideNoCost || !(fact.row.flags.includes('NO_COST') || fact.row.flags.includes('COST_UNKNOWN')))
    && (!filters.manager.length || filters.manager.includes(fact.keys.manager.key))
    && (!filters.customer.length || filters.customer.includes(fact.keys.customer.key))
    && (!filters.product.length || filters.product.includes(fact.keys.product.key));
}

export function buildOrderPivot(rows: OrderInResultRow[], info: Map<string, PivotOrderInfo>, options: PivotOptions): PivotResult {
  const { first, columns = 'total', scope = 'all', filters = NO_PIVOT_FILTERS, unknownProduct = 'Bez produktu z katalogu' } = options;
  const second = options.second && options.second !== first ? options.second : null;
  const withProducts = first === 'product' || second === 'product' || filters.product.length > 0;
  const list = facts(rows, info, withProducts, unknownProduct, scope).filter((fact) => matchesFilters(fact, filters));
  const total = emptyCells();
  list.forEach((fact) => addToCells(total, fact, columns));
  const months = list.map((fact) => fact.month).filter(Boolean).sort();
  const buckets = columns === 'total' || months.length === 0
    ? []
    : bucketRange(bucketOf(months[0], columns), bucketOf(months[months.length - 1], columns), columns);
  return { nodes: group(list, first, second, columns), buckets, ...total };
}

/** Choices for the filters: every manager, customer and product in the rows, by value. */
export function pivotFilterOptions(rows: OrderInResultRow[], info: Map<string, PivotOrderInfo>, unknownProduct = 'Bez produktu z katalogu'): Record<PivotDimension, PivotFilterOption[]> {
  const all = facts(rows, info, true, unknownProduct, 'all');
  const options = (dimension: PivotDimension) => {
    const byKey = new Map<string, { label: string; value: number }>();
    for (const fact of all) {
      const identity = fact.keys[dimension];
      const current = byKey.get(identity.key);
      byKey.set(identity.key, { label: identity.label, value: (current?.value || 0) + fact.revenue });
    }
    return Array.from(byKey.entries()).sort((a, b) => b[1].value - a[1].value).map(([key, { label }]) => ({ key, label }));
  };
  return { manager: options('manager'), customer: options('customer'), product: options('product') };
}

/** Order statuses present in the rows (outside the sums left out), most orders first. */
export function pivotStatusOptions(rows: OrderInResultRow[]): string[] {
  const counts = new Map<string, number>();
  rows.filter((row) => row.place !== 'OUTSIDE' && row.status).forEach((row) => counts.set(row.status, (counts.get(row.status) || 0) + 1));
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map(([status]) => status);
}

/**
 * Sort key of a row: a column of the total view (`invoiced`, `cost`, …), a
 * bucket (`2026-03`) or `total` of the period view (the chosen measure), or
 * `label`. Descending by default; `.asc` in the address turns it.
 */
export interface PivotSort {
  key: string;
  direction: 'asc' | 'desc';
}

const TOTAL_VIEW_MEASURE: Record<string, PivotMeasure | ((money: PivotMoney) => number)> = {
  orders: 'orders',
  invoiced: (money) => money.invoiced,
  waiting: (money) => money.waiting,
  cost: 'cost',
  margin: 'margin',
  marginPercent: 'marginPercent',
};

export function pivotSortValue(cells: PivotCells, sortKey: string, measure: PivotMeasure): number | null {
  const fixed = TOTAL_VIEW_MEASURE[sortKey];
  if (typeof fixed === 'function') return cells.total.orderIds.size ? fixed(cells.total) : null;
  if (fixed) return measureValue(cells.total, fixed);
  if (sortKey === 'total') return measureValue(cells.total, measure);
  return measureValue(cells.byBucket.get(sortKey), measure);
}

/**
 * The same pivot with every level (groups, second level, orders) in the
 * chosen order; empty cells last either way. The table and the Excel export
 * read this order, so the file follows the screen.
 */
export function sortPivot(result: PivotResult, sort: PivotSort | null, measure: PivotMeasure): PivotResult {
  if (!sort) return result;
  const sign = sort.direction === 'asc' ? 1 : -1;
  const compare = (aLabel: string, a: PivotCells, bLabel: string, b: PivotCells): number => {
    if (sort.key === 'label') return sign * aLabel.localeCompare(bLabel, 'pl');
    const x = pivotSortValue(a, sort.key, measure);
    const y = pivotSortValue(b, sort.key, measure);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return sign * (x - y);
  };
  const sortNodes = (nodes: PivotNode[]): PivotNode[] => nodes
    .map((node) => ({
      ...node,
      children: sortNodes(node.children),
      orders: [...node.orders].sort((a, b) => compare(a.row.displayOrderNumber, a, b.row.displayOrderNumber, b)),
    }))
    .sort((a, b) => compare(a.label, a, b.label, b));
  return { ...result, nodes: sortNodes(result.nodes) };
}

/**
 * The chosen measure of a cell; null when it cannot be told: margin % of no
 * revenue, or an empty cell (no orders) for any measure.
 */
export function measureValue(money: PivotMoney | undefined, measure: PivotMeasure): number | null {
  if (!money || money.orderIds.size === 0) return null;
  const revenue = money.invoiced + money.waiting;
  if (measure === 'orders') return money.orderIds.size;
  if (measure === 'revenue') return revenue;
  if (measure === 'margin') return money.margin;
  if (measure === 'cost') return money.cost;
  // Margin % only from the orders with a complete cost; none → unknown („—”).
  return revenue > 0 ? (money.margin / revenue) * 100 : null;
}

/**
 * Change against the previous bucket: percent for amounts and counts,
 * percentage points for margin %. null when there is nothing to compare.
 */
export function measureChange(current: PivotMoney | undefined, previous: PivotMoney | undefined, measure: PivotMeasure): number | null {
  const now = measureValue(current, measure);
  const before = measureValue(previous, measure);
  if (now == null || before == null) return null;
  if (measure === 'marginPercent') return now - before;
  if (before === 0) return null;
  return ((now - before) / Math.abs(before)) * 100;
}

/** Margin incomplete in this cell: the measure leaves out an unknown cost. */
export function measureIncomplete(money: PivotMoney | undefined, measure: PivotMeasure): boolean {
  return !!money && money.marginIncomplete && (measure === 'margin' || measure === 'cost' || measure === 'marginPercent');
}

export function pivotTotal(nodes: PivotNode[]): PivotMoney {
  const total = emptyMoney();
  for (const node of nodes) {
    node.orderIds.forEach((id) => total.orderIds.add(id));
    total.invoiced += node.invoiced;
    total.waiting += node.waiting;
    total.margin += node.margin;
    total.cost += node.cost;
    total.costActual += node.costActual;
    total.marginActual += node.marginActual;
    mergePendingScope(total.pending, node.pending);
  }
  total.pendingOrderIds = pendingOrderIds(total.pending);
  total.marginIncomplete = total.pendingOrderIds.size > 0;
  return total;
}
